import { ApiErrorPageWithTrans } from '@components/Error/ApiErrorPageWithTrans';
import { ContentWrapperCustomerApp } from '@components/Layouts/ContentWrapperCustomerApp';
import { NoddiLoadingScreenWithTrans } from '@components/Loading/NoddiLoadingScreenWithTrans';
import { t } from '@lingui/core/macro';
import { BrandRecordList, PlaceLocationRecord } from '@navio/nsdk';
import { keepPreviousData } from '@tanstack/react-query';
import { noddiAsync, URLKeys } from 'noddi-async';
import {
  ApiErrorMessageInternal,
  ApiErrorPageInternal,
  LoadingScreen,
  NoddiMultiSelect,
  noddiToast,
  useNoddiQueryParam
} from 'noddi-ui-web';
import {
  ErrorPage,
  MapLegend,
  MapLegendItem,
  NoddiLeafletMapContainer,
  ServiceAreaIcon,
  useAddressMapNavigation
} from 'noddi-web';
import { NumericArrayParam, withDefault } from 'use-query-params';
import { useCurrentBrand } from '../../stores/BrandStore/brandStore';
import { getCommonTranslations } from '../../utils/commonTranslations';
import { translateServiceCategory } from '../../utils/translateBookingCategory';
import { DeliveryMapContent } from './DeliveryMapContent';

export const DeliveryMap = () => {
  const { currentBrand } = useCurrentBrand();

  const {
    data: brand,
    isPending: isBrandPending,
    error: brandError
  } = noddiAsync.useGet({
    type: URLKeys.getByDomainBrand,
    input: {
      brandDomain: currentBrand
    }
  });

  if (brandError) {
    return <ApiErrorPageWithTrans error={brandError} />;
  }

  if (isBrandPending) {
    return <NoddiLoadingScreenWithTrans />;
  }
  return <DeliveryMapWrapper brandData={brand} />;
};

const DeliveryMapWrapper = ({ brandData }: { brandData: BrandRecordList }) => {
  const { serviceDepartmentServiceCategoryIds, setServiceDepartmentServiceCategoryIds } =
    useServiceDepartmentServiceCategoryIdsQueryParam();

  const brandDomains: string[] = [
    // @ts-expect-error ignore ts error until noddi-ts package is updated
    ...brandData.includedBrands.map((b: { domain: string }) => b.domain),
    brandData.domain
  ];

  const {
    isPending: isAddressPending,
    error: addressError,
    handleSelect,
    handleClear
  } = useAddressMapNavigation({
    brandDomains,
    onAddressResolved: (resolved) => {
      if (resolved.isInDeliveryArea) {
        noddiToast.success(t`Great news! We deliver to this address.`);
      } else {
        noddiToast.error(t`Unfortunately, we don't deliver to this address yet.`);
      }
    }
  });

  const {
    data: serviceCategories,
    isPending: isServiceCategoriesPending,
    error: serviceCategoriesError
  } = noddiAsync.useGet({
    type: URLKeys.getPaginatedServiceCategories,
    input: {
      pageIndex: 0,
      pageSize: 100,
      brandDomainsIncludeMarketplace: [brandData.domain]
    }
  });

  const {
    data: serviceAreas,
    isRefetching: isServiceAreasRefetching,
    isPending: isServiceAreasPending,
    error: serviceAreasError
  } = noddiAsync.useGet({
    type: URLKeys.getServiceAreasForDeliveryMap,
    input: {
      serviceCategoryIds: serviceDepartmentServiceCategoryIds,
      brandDomains
    },
    queryConfig: {
      placeholderData: keepPreviousData
    }
  });

  if (serviceCategoriesError || serviceAreasError) {
    return (
      <ContentWrapperCustomerApp className='mx-auto max-w-6xl'>
        <ApiErrorPageInternal error={[serviceCategoriesError, serviceAreasError]} />
      </ContentWrapperCustomerApp>
    );
  }

  if (isServiceCategoriesPending || isServiceAreasPending) {
    return <LoadingScreen />;
  }

  if (serviceAreas?.length === 0) {
    return (
      <ContentWrapperCustomerApp className='mx-auto max-w-6xl' title={t`Delivery map`}>
        <ErrorPage
          title={t`No delivery areas found`}
          description={t`Unfortunately, we couldn't find any delivery areas at the moment. Please contact our support.`}
        />
      </ContentWrapperCustomerApp>
    );
  }

  const serviceCategoryOptions =
    serviceCategories.results.map((sc) => ({ label: translateServiceCategory(sc.name), value: sc.id })) || [];

  const selectedServiceCategoryOptions = serviceCategoryOptions.filter((option) =>
    serviceDepartmentServiceCategoryIds.includes(option.value)
  );

  return (
    <ContentWrapperCustomerApp
      showNewBookingButton
      title={t`Delivery map`}
      subtitle={t`Check if we can deliver services to you!`}
    >
      {serviceCategoryOptions.length > 1 && (
        <div className='mb-4 w-fit'>
          <NoddiMultiSelect
            className='w-fit'
            label={t`Filter the areas in the map by specific product categories`}
            hideSelectAll
            searchable={false}
            placeholder={t`Select product categories`}
            options={serviceCategoryOptions}
            defaultValue={selectedServiceCategoryOptions.map((option) => option.value)}
            onChange={(selectedValues) => setServiceDepartmentServiceCategoryIds(selectedValues as number[])}
          />
        </div>
      )}

      {addressError && <ApiErrorMessageInternal error={addressError} />}

      <div className='relative'>
        <NoddiLeafletMapContainer
          loading={isServiceAreasRefetching || isAddressPending}
          placeSearchTranslations={getCommonTranslations().placeSearch}
          // oxlint-disable-next-line typescript/no-misused-promises -- async onPlaceSelected runs handleSelect(); React discards the returned promise, so a rejection is unhandled unless the body catches
          onPlaceSelected={async (location: PlaceLocationRecord) => {
            // City-level results should only move the map. Delivery-area lookup starts once we have a full address.
            if (!location.addressInfo) {
              return;
            }

            await handleSelect({ placeId: location.placeId });
          }}
          onPlaceClear={handleClear}
        >
          <DeliveryMapContent serviceAreas={serviceAreas ?? []} />
        </NoddiLeafletMapContainer>
        <MapLegend>
          <MapLegendItem icon={<ServiceAreaIcon variant='selected' />}>{t`Delivery area`}</MapLegendItem>
        </MapLegend>
      </div>
    </ContentWrapperCustomerApp>
  );
};

const useServiceDepartmentServiceCategoryIdsQueryParam = () => {
  const [serviceDepartmentServiceCategoryIdsWithNull, setServiceDepartmentServiceCategoryIds] = useNoddiQueryParam(
    'service-category-ids',
    withDefault(NumericArrayParam, [])
  );

  const serviceDepartmentServiceCategoryIds = serviceDepartmentServiceCategoryIdsWithNull as number[];

  return { serviceDepartmentServiceCategoryIds, setServiceDepartmentServiceCategoryIds };
};
